import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useStore, CATEGORIES } from '../context/ProductStore';

/* ─── Cart Dropdown ──────────────────────────────────────── */
const CartDropdown = ({ isDark, cartCount }) => {
  const { cart, products, removeFromCart } = useStore();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const cartItems = cart.map(item => {
    const product = products.find(p => p.id === item.id);
    return product ? { ...product, qty: item.qty } : null;
  }).filter(Boolean);

  const total = cartItems.reduce((sum, item) => {
    const price = item.isPromo && item.promoPrice ? parseFloat(item.promoPrice) : parseFloat(item.price);
    return sum + price * item.qty;
  }, 0);

  return (
    <div style={{ position: 'relative' }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <div style={styles.navIconBtn(isDark)}>
        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          <span style={styles.navBadge}>{cartCount}</span>
        </span>
      </div>

      {open && (
        <div style={styles.cartDropdown(isDark)}>
          <div style={styles.cartDropdownTitle(isDark)}>
            Your Cart {cartCount > 0 && <span style={styles.cartCountBadge}>{cartCount}</span>}
          </div>
          {cartItems.length === 0 ? (
            <div style={styles.cartEmpty}>
              <span style={{ fontSize: 32 }}>🛒</span>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280', fontFamily: "Inter, sans-serif" }}>Your cart is empty</p>
              <button onClick={() => { navigate('/shop'); setOpen(false); }} style={styles.cartShopBtn}>Start Shopping</button>
            </div>
          ) : (
            <>
              <div style={styles.cartItemsList}>
                {cartItems.slice(0, 4).map(item => {
                  const price = item.isPromo && item.promoPrice ? parseFloat(item.promoPrice) : parseFloat(item.price);
                  const img = item.images?.[0] || item.image || null;
                  return (
                    <div key={item.id} style={styles.cartItemRow(isDark)}>
                      <div style={styles.cartItemImg}>
                        {img ? <img src={img} alt={item.name} style={{ width:'100%', height:'100%', objectFit:'contain' }} /> : <span style={{ fontSize: 20 }}>📦</span>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={styles.cartItemName(isDark)}>{item.name}</div>
                        <div style={styles.cartItemPrice}>USD {price.toFixed(2)} × {item.qty}</div>
                      </div>
                      <button onClick={() => removeFromCart(item.id)} style={styles.cartRemoveBtn}>✕</button>
                    </div>
                  );
                })}
                {cartItems.length > 4 && (
                  <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', padding: '4px 0', fontFamily: "Inter, sans-serif" }}>
                    +{cartItems.length - 4} more item{cartItems.length - 4 > 1 ? 's' : ''}
                  </div>
                )}
              </div>
              <div style={styles.cartDropdownDivider} />
              <div style={styles.cartTotalRow(isDark)}>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600 }}>Total</span>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 15, fontWeight: 800, color: '#CC0000' }}>USD {total.toFixed(2)}</span>
              </div>
              <button onClick={() => { navigate('/shop'); setOpen(false); }} style={styles.cartViewBtn}>View Cart & Checkout</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Category dropdown inside search bar ────────────────── */
const SEARCH_CATEGORIES = [
  { label: 'All Categories', value: '' },
  { label: '── Laptops', value: 'Laptops', isGroup: true },
  { label: 'Business Laptops',    value: 'Business Laptops' },
  { label: 'Gaming Laptops',      value: 'Gaming Laptops' },
  { label: 'Student Laptops',     value: 'Student Laptops' },
  { label: '2-in-1 Laptops',      value: '2-in-1 Laptops' },
  { label: 'Ultrabooks',          value: 'Ultrabooks' },
  { label: '── Desktops', value: 'Desktops', isGroup: true },
  { label: 'All in One Desktops', value: 'All in One Desktops' },
  { label: 'Desktops',            value: 'Desktops' },
  { label: 'Monitors',            value: 'Monitors' },
  { label: 'Power Supply & Graphic Cards', value: 'Power Supply & Graphic Cards' },
  { label: '── Phones', value: 'Phones', isGroup: true },
  { label: 'Smartphones',         value: 'Smartphones' },
  { label: 'Tablets',             value: 'Tablets' },
  { label: 'Feature Phones',      value: 'Feature Phones' },
  { label: '── Printers', value: 'Printers', isGroup: true },
  { label: 'Inkjet Printers',     value: 'Inkjet Printers' },
  { label: 'Laser Printers',      value: 'Laser Printers' },
  { label: 'Multifunction Printers', value: 'Multifunction Printers' },
  { label: 'Ink & Toners',        value: 'Ink & Toners' },
  { label: '── Networking', value: 'Networking', isGroup: true },
  { label: 'Routers & Switches',  value: 'Routers & Switches' },
  { label: 'Starlink',            value: 'Starlink' },
  { label: 'WiFi Extenders',      value: 'WiFi Extenders' },
  { label: 'Mikrotik',            value: 'Mikrotik' },
  { label: '── Accessories', value: 'Accessories', isGroup: true },
  { label: 'Keyboards & Mice',    value: 'Keyboards & Mice' },
  { label: 'Headsets & Audio',    value: 'Headsets & Audio' },
  { label: 'Webcams',             value: 'Webcams' },
  { label: 'USB Hubs',            value: 'USB Hubs' },
  { label: '── PA Systems', value: 'PA Systems', isGroup: true },
  { label: 'Speakers',            value: 'Speakers' },
  { label: 'Microphones',         value: 'Microphones' },
  { label: 'Mixers',              value: 'Mixers' },
  { label: 'Amplifiers',          value: 'Amplifiers' },
  { label: '── Laptop Bags', value: 'Laptop Bags', isGroup: true },
  { label: 'Backpacks',           value: 'Backpacks' },
  { label: 'Sleeve Cases',        value: 'Sleeve Cases' },
  { label: 'Trolley Bags',        value: 'Trolley Bags' },
  { label: 'Messenger Bags',      value: 'Messenger Bags' },
];

const CategoryDropdown = ({ isDark, onCategoryChange }) => {
  const [open, setOpen]     = useState(false);
  const [selected, setSelected] = useState('All Categories');
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (item) => {
    setSelected(item.label);
    setOpen(false);
    if (onCategoryChange) onCategoryChange(item.value);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={styles.categoryBtn(isDark)}>
        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
          {selected}
        </span>
        <span style={{ fontSize: 10, marginLeft: 6, transform: open ? 'rotate(180deg)' : 'none', display: 'inline-block', transition: 'transform 0.15s', flexShrink: 0 }}>▾</span>
      </button>
      {open && (
        <div style={styles.categoryDropdown(isDark)}>
          {SEARCH_CATEGORIES.map((item, i) => (
            item.isGroup ? (
              <div key={i} style={{
                padding: '8px 14px 4px',
                fontSize: 10, fontWeight: 800, letterSpacing: '0.1em',
                color: '#CC0000', textTransform: 'uppercase',
                fontFamily: "'Inter', sans-serif",
                borderTop: i > 0 ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}` : 'none',
                marginTop: i > 0 ? 4 : 0,
              }}>
                {item.label.replace('── ', '')}
              </div>
            ) : (
              <button key={i} onClick={() => handleSelect(item)} style={{
                ...styles.categoryOption(isDark),
                paddingLeft: item.value === '' ? 14 : 22,
                fontWeight: item.value === '' ? 700 : 400,
                color: item.value === '' ? (isDark ? '#fff' : '#111') : (isDark ? 'rgba(255,255,255,0.8)' : '#444'),
              }}
                onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : '#f0f4ff'; e.currentTarget.style.color = '#CC0000'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = item.value === '' ? (isDark ? '#fff' : '#111') : (isDark ? 'rgba(255,255,255,0.8)' : '#444'); }}
              >
                {item.label}
              </button>
            )
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Shop mega-menu ─────────────────────────────────────── */
const SHOP_CATEGORIES = [
  { label: 'Laptops',      path: '/shop?category=Laptops',     icon: '💻',
    subs: ['Business Laptops', 'Gaming Laptops', 'Student Laptops', '2-in-1 Laptops', 'Ultrabooks'] },
  { label: 'Desktops',     path: '/shop?category=Desktops',    icon: '🖥️',
    subs: ['All in One Desktops', 'Desktops', 'Monitors', 'Power Supply & Graphic Cards'] },
  { label: 'Phones',       path: '/shop?category=Phones',      icon: '📱',
    subs: ['Smartphones', 'Tablets', 'Feature Phones', 'Phone Accessories'] },
  { label: 'Printers',     path: '/shop?category=Printers',    icon: '🖨️',
    subs: ['Inkjet Printers', 'Laser Printers', 'Multifunction Printers', 'Ink & Toners'] },
  { label: 'Networking',   path: '/shop?category=Networking',  icon: '🌐',
    subs: ['Routers & Switches', 'Starlink', 'WiFi Extenders', 'Cables & Adapters', 'Mikrotik'] },
  { label: 'Accessories',  path: '/shop?category=Accessories', icon: '🎧',
    subs: ['Keyboards & Mice', 'Headsets & Audio', 'Webcams', 'USB Hubs', 'Cables'] },
  { label: 'PA Systems',   path: '/shop?category=PA Systems',  icon: '🔊',
    subs: ['Speakers', 'Microphones', 'Mixers', 'Amplifiers'] },
  { label: 'Laptop Bags',  path: '/shop?category=Laptop Bags', icon: '🎒',
    subs: ['Backpacks', 'Sleeve Cases', 'Trolley Bags', 'Messenger Bags'] },
];

const ShopDropdown = ({ isActive }) => {
  const [open, setOpen]         = useState(false);
  const [hoveredCat, setHoveredCat] = useState(null);
  const ref     = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setHoveredCat(null); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const activeCat = hoveredCat ?? SHOP_CATEGORIES[0];

  return (
    <div ref={ref}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'stretch' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => { setOpen(false); setHoveredCat(null); }}
    >
      {/* "Shop" button */}
      <button onClick={() => { navigate('/shop'); setOpen(false); }} style={styles.shopNavBtn(isActive)}>
        Shop
        <span style={{ fontSize: 10, marginLeft: 5, display: 'inline-block', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
      </button>

      {open && (
        <div style={styles.shopMegaMenu}>
          {/* Left column — category list */}
          <div style={styles.shopMegaLeft}>
            <div style={styles.shopDropdownHeader}>SHOP BY CATEGORY</div>
            {SHOP_CATEGORIES.map(cat => (
              <div
                key={cat.label}
                onMouseEnter={() => setHoveredCat(cat)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 16px', cursor: 'pointer', gap: 10,
                  background: (hoveredCat ?? SHOP_CATEGORIES[0]).label === cat.label
                    ? '#f0f4ff' : 'transparent',
                  borderLeft: (hoveredCat ?? SHOP_CATEGORIES[0]).label === cat.label
                    ? '3px solid #CC0000' : '3px solid transparent',
                  transition: 'all 0.1s',
                }}
                onClick={() => { navigate(cat.path); setOpen(false); setHoveredCat(null); }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{cat.icon}</span>
                  <span style={{
                    fontSize: 14, fontWeight: 500, fontFamily: "'Inter', sans-serif",
                    color: (hoveredCat ?? SHOP_CATEGORIES[0]).label === cat.label ? '#CC0000' : '#1a1a2e',
                  }}>{cat.label}</span>
                </div>
                <span style={{ fontSize: 11, color: '#aaa' }}>›</span>
              </div>
            ))}
            <div style={styles.shopDropdownDivider} />
            <div
              style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', cursor:'pointer' }}
              onClick={() => { navigate('/shop'); setOpen(false); }}
              onMouseEnter={e => e.currentTarget.style.background='#fff0f0'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
            >
              <span style={{ fontSize: 18 }}>🛍️</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#CC0000', fontFamily: "'Inter', sans-serif" }}>View All Products</span>
            </div>
          </div>

          {/* Right column — subcategories of hovered cat */}
          <div style={styles.shopMegaRight}>
            <div style={styles.shopDropdownHeader}>{activeCat.label.toUpperCase()}</div>
            {activeCat.subs.map(sub => (
              <div
                key={sub}
                style={{ padding: '9px 16px', cursor: 'pointer', fontSize: 14, fontFamily: "'Inter', sans-serif", color: '#374151', transition: 'all 0.1s', borderRadius: 6 }}
                onClick={() => { navigate(`${activeCat.path}&sub=${encodeURIComponent(sub)}`); setOpen(false); setHoveredCat(null); }}
                onMouseEnter={e => { e.currentTarget.style.background='#f0f4ff'; e.currentTarget.style.color='#CC0000'; }}
                onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#374151'; }}
              >
                {sub}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Social icons ───────────────────────────────────────── */
const SOCIAL_LINKS = [
  { label: 'Facebook',  href: 'https://www.facebook.com/techdavesolutions/',
    svg: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg> },
  { label: 'X',         href: 'https://x.com/',
    svg: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
  { label: 'Instagram', href: 'https://instagram.com/',
    svg: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg> },
  { label: 'WhatsApp',  href: 'https://wa.me/263772758875',
    svg: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg> },
];

/* ─── Auth helpers ───────────────────────────────────────── */
const CUSTOMERS_KEY = 'techdave_customers';
const SESSION_KEY   = 'techdave_session';
const getCustomers  = () => JSON.parse(localStorage.getItem(CUSTOMERS_KEY) || '[]');
const saveCustomers = (list) => localStorage.setItem(CUSTOMERS_KEY, JSON.stringify(list));
const getSession    = () => JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
const saveSession   = (u) => localStorage.setItem(SESSION_KEY, JSON.stringify(u));
const clearSession  = () => localStorage.removeItem(SESSION_KEY);

/* ─── Main Header ────────────────────────────────────────── */
const Header = ({ cartCount = 0, isDark = false, onToggleDark }) => {
  const [menuOpen,     setMenuOpen]     = useState(false);
  const [searchVal,    setSearchVal]    = useState('');
  const [searchCat,    setSearchCat]    = useState('');  // category selected in search bar
  const [accountOpen,  setAccountOpen]  = useState(false);
  const [customer,     setCustomer]     = useState(getSession());
  const [showAuth,     setShowAuth]     = useState(false);
  const [authMode,     setAuthMode]     = useState('login');
  const [authName,     setAuthName]     = useState('');
  const [authEmail,    setAuthEmail]    = useState('');
  const [authPhone,    setAuthPhone]    = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError,    setAuthError]    = useState('');
  const [authSuccess,  setAuthSuccess]  = useState('');

  const accountRef = useRef(null);
  const navigate   = useNavigate();
  const location   = useLocation();
  const headerRef  = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (headerRef.current && !headerRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const isActive   = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path;
  };
  const isShopActive = () => location.pathname === '/shop';

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchVal.trim()) params.set('search', searchVal.trim());
    if (searchCat)        params.set('category', searchCat);
    if (searchVal.trim() || searchCat) {
      navigate(`/shop?${params.toString()}`);
      setSearchVal('');
    }
  };

  const openAuth = (mode) => {
    setAuthMode(mode); setAuthError(''); setAuthSuccess('');
    setAuthName(''); setAuthEmail(''); setAuthPhone(''); setAuthPassword('');
    setAccountOpen(false); setShowAuth(true);
  };

  const handleSignup = () => {
    setAuthError(''); setAuthSuccess('');
    if (!authName || !authEmail || !authPassword) return setAuthError('Please fill in all required fields.');
    if (authPassword.length < 4) return setAuthError('Password must be at least 4 characters.');
    const customers = getCustomers();
    if (customers.find(c => c.email === authEmail)) return setAuthError('An account with this email already exists.');
    const newCustomer = { id: Date.now(), name: authName, email: authEmail, phone: authPhone, password: authPassword, joinedAt: new Date().toISOString(), purchases: 0, isRegular: false };
    saveCustomers([...customers, newCustomer]); saveSession(newCustomer); setCustomer(newCustomer);
    setAuthSuccess(`Welcome, ${authName}! Your account is ready.`);
    setTimeout(() => { setShowAuth(false); resetForm(); }, 1500);
  };

  const handleLogin = () => {
    setAuthError(''); setAuthSuccess('');
    if (!authEmail || !authPassword) return setAuthError('Please enter your email and password.');
    const found = getCustomers().find(c => c.email === authEmail && c.password === authPassword);
    if (!found) return setAuthError('Invalid email or password.');
    saveSession(found); setCustomer(found);
    setAuthSuccess(`Welcome back, ${found.name}!`);
    setTimeout(() => { setShowAuth(false); resetForm(); }, 1200);
  };

  const handleLogout = () => { clearSession(); setCustomer(null); setAccountOpen(false); };

  const resetForm = () => {
    setAuthName(''); setAuthEmail(''); setAuthPhone(''); setAuthPassword('');
    setAuthError(''); setAuthSuccess('');
  };

  // Total header height: 76px (white) + 34px (red) + 44px (navy) = 154px
  return (
    <>
      <style>{`
        .td-hamburger { display: none !important; }
        .td-mob-search { display: none; }
        @media (max-width: 900px) {
          .td-desktop-search { display: none !important; }
          .td-red-bar { display: none !important; }
          .td-hamburger { display: flex !important; }
          .td-mob-search { display: block !important; }
          .td-login-text { display: none !important; }
        }
        .td-nav-link:hover  { background: rgba(255,255,255,0.1) !important; }
        .td-shop-btn:hover  { background: rgba(255,255,255,0.1) !important; }
        .td-social-a:hover  { background: rgba(255,255,255,0.25) !important; }
      `}</style>

      <header ref={headerRef} style={styles.outerWrap}>

        {/* ── ROW 1: White bar — Logo | Search | Login | Wishlist | Cart ── */}
        <div style={styles.whiteBar(isDark)}>
          <div style={styles.whiteBarInner}>

            {/* Logo */}
            <Link to="/" style={styles.logoWrap}>
              <img src="/techdave-logo.png" alt="TechDave Solutions" style={styles.logoImg} />
              <div style={styles.logoText}>
                <span style={styles.logoMain}>TECHDAVE</span>
                <span style={styles.logoSub(isDark)}>SOLUTIONS</span>
              </div>
            </Link>

            {/* Search bar */}
            <form onSubmit={handleSearch} style={styles.searchBarWrap(isDark)} className="td-desktop-search">
              <input type="text" value={searchVal} onChange={e => setSearchVal(e.target.value)}
                placeholder="Search for products…" style={styles.searchBarInput(isDark)} />
              <div style={styles.searchBarDivider(isDark)}>
                <CategoryDropdown isDark={isDark} onCategoryChange={setSearchCat} />
              </div>
              <button type="submit" style={styles.searchBarBtn} aria-label="Search">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>
            </form>

            {/* Right group */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexShrink: 0 }}>

              {/* Login / Account */}
              <div ref={accountRef} style={{ position: 'relative' }}
                onMouseEnter={() => setAccountOpen(true)} onMouseLeave={() => setAccountOpen(false)}>
                <span style={styles.loginLink(isDark)}>
                  {customer ? `Hi, ${customer.name.split(' ')[0]}` : 'LOGIN / REGISTER'}
                </span>
                {accountOpen && (
                  <div style={styles.accountDropdown(isDark)}>
                    {customer ? (
                      <>
                        <div style={styles.dropdownTitle(isDark)}>Hi, {customer.name.split(' ')[0]}!</div>
                        <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', fontFamily: "Inter, sans-serif" }}>{customer.email}</div>
                        <div style={styles.dropdownDivider} />
                        {[{ icon:'👤', label:'My Account', to:'/account' }, { icon:'🛒', label:'My Orders', to:'/orders' }, { icon:'💻', label:'My Devices', to:'/devices' }].map(item => (
                          <Link key={item.label} to={item.to} style={styles.dropdownItem(isDark)} onClick={() => setAccountOpen(false)}>
                            <span>{item.icon}</span><span>{item.label}</span>
                          </Link>
                        ))}
                        <div style={styles.dropdownDivider} />
                        <button onClick={handleLogout} style={styles.dropdownLogoutBtn}>Sign Out</button>
                      </>
                    ) : (
                      <>
                        <div style={styles.dropdownTitle(isDark)}>Welcome!</div>
                        <button onClick={() => openAuth('login')} style={styles.dropdownSignIn}>Sign in</button>
                        <button onClick={() => openAuth('signup')} style={styles.dropdownCreateBtn}>Create an account</button>
                        <p style={styles.dropdownSubtext}>Faster checkout, order tracking & exclusive deals.</p>
                        <div style={styles.dropdownDivider} />
                        {[{ icon:'👤', label:'Account', to:'/account' }, { icon:'🛒', label:'Orders', to:'/orders' }, { icon:'💻', label:'Devices', to:'/devices' }].map(item => (
                          <Link key={item.label} to={item.to} style={styles.dropdownItem(isDark)} onClick={() => setAccountOpen(false)}>
                            <span>{item.icon}</span><span>{item.label}</span>
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Wishlist */}
              <Link to="/account" style={styles.navIconBtn(isDark)} title="Wishlist">
                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  <span style={styles.navBadge}>0</span>
                </span>
              </Link>

              {/* Cart */}
              <CartDropdown isDark={isDark} cartCount={cartCount} />

              {onToggleDark && (
                <button onClick={onToggleDark} style={{ background:'none', border:'none', cursor:'pointer', fontSize:16, color: isDark?'#fff':'#555', padding:'2px 4px' }} title="Toggle theme">
                  {isDark ? '☀️' : '🌙'}
                </button>
              )}
            </div>

            {/* Mobile hamburger */}
            <button className="td-hamburger" style={styles.hamburger(isDark)} onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu">
              <span style={styles.bar(isDark)} /><span style={styles.bar(isDark)} /><span style={styles.bar(isDark)} />
            </button>
          </div>
        </div>

        {/* ── ROW 2: Single red bar — nav links | phones+email | FB+WA ── */}
        <div style={styles.redNavBar} className="td-red-bar">
          <div style={styles.redNavBarInner}>

            {/* Left: nav links */}
            <div style={{ display: 'flex', alignItems: 'stretch', flexShrink: 0 }}>
              <Link to="/"        style={styles.navLink(isActive('/'))}        className="td-nav-link">Home</Link>
              <Link to="/shop" style={styles.navLink(isShopActive())} className="td-nav-link">Shop</Link>
              <Link to="/about"   style={styles.navLink(isActive('/about'))}   className="td-nav-link">About Us</Link>
              <Link to="/contact" style={styles.navLink(isActive('/contact'))} className="td-nav-link">Contact</Link>
              <Link to="/faqs"    style={styles.navLink(isActive('/faqs'))}    className="td-nav-link">FAQs</Link>
            </div>

            {/* Centre: phone numbers (both WhatsApp-linked) + email */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, justifyContent: 'center' }}>
              <a href="https://wa.me/263772758875" target="_blank" rel="noopener noreferrer" style={styles.redBarLink} title="Chat on WhatsApp">
                📞 +263 772 758 875
              </a>
              <span style={{ color: 'rgba(255,255,255,0.35)' }}>|</span>
              <a href="https://wa.me/263785661758" target="_blank" rel="noopener noreferrer" style={styles.redBarLink} title="Chat on WhatsApp">
                📞 +263 785 661 758
              </a>
              <span style={{ color: 'rgba(255,255,255,0.35)' }}>|</span>
              <a href="mailto:sales@techdavesolutions.co.zw" style={styles.redBarLink}>
                sales@techdavesolutions.co.zw
              </a>
            </div>

            {/* Right: Facebook + WhatsApp only */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {/* Facebook */}
              <a href="https://www.facebook.com/techdavesolutions/" target="_blank" rel="noopener noreferrer"
                title="Facebook" className="td-social-a" style={styles.redBarSocialIcon}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
              </a>
              {/* WhatsApp */}
              <a href="https://wa.me/263772758875" target="_blank" rel="noopener noreferrer"
                title="WhatsApp" className="td-social-a" style={styles.redBarSocialIcon}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                </svg>
              </a>
            </div>

          </div>
        </div>

        {/* Mobile search */}
        <div className="td-mob-search" style={{ padding:'8px 14px', borderTop:'1px solid #eee', background:'#fff' }}>
          <form onSubmit={handleSearch} style={{ display:'flex', alignItems:'center', gap:8, background:'#f5f5f5', border:'1px solid #ddd', borderRadius:20, padding:'6px 14px' }}>
            <input type="text" value={searchVal} onChange={e => setSearchVal(e.target.value)} placeholder="Search products…"
              style={{ flex:1, border:'none', outline:'none', background:'transparent', fontSize:13, color:'#333', fontFamily:"Inter, sans-serif" }} />
            <button type="submit" style={{ background:'none', border:'none', cursor:'pointer', color:'#CC0000', fontSize:16 }}>🔍</button>
          </form>
        </div>

        {/* Mobile slide-down menu */}
        <div style={{ display:'flex', flexDirection:'column', overflow:'hidden', maxHeight: menuOpen?640:0, transition:'max-height 0.3s ease', background:'#fff', borderTop: menuOpen?'1px solid #eee':'none' }}>
          <Link to="/"        style={{ padding:'12px 20px', color: isActive('/')?'#fff':'#222', backgroundColor: isActive('/')?'#CC0000':'transparent', textDecoration:'none', display:'block', fontFamily:"'Inter', sans-serif", fontSize:15, fontWeight: isActive('/')?700:500, borderBottom:'1px solid #f0f0f0' }} onClick={() => setMenuOpen(false)}>🏠 Home</Link>
          <Link to="/shop"    style={{ padding:'12px 20px', color: isShopActive()?'#fff':'#222', backgroundColor: isShopActive()?'#CC0000':'transparent', textDecoration:'none', display:'block', fontFamily:"'Inter', sans-serif", fontSize:15, fontWeight: isShopActive()?700:500, borderBottom:'1px solid #f0f0f0' }} onClick={() => setMenuOpen(false)}>🛍️ Shop</Link>
          {SHOP_CATEGORIES.map(cat => (
            <Link key={cat.label} to={cat.path}
              style={{ padding:'9px 20px 9px 36px', color:'#555', textDecoration:'none', display:'block', fontFamily:"'Inter', sans-serif", fontSize:13, borderBottom:'1px solid #f8f8f8' }}
              onClick={() => setMenuOpen(false)}>{cat.icon} {cat.label}</Link>
          ))}
          <Link to="/about"   style={{ padding:'12px 20px', color: isActive('/about')?'#fff':'#222', backgroundColor: isActive('/about')?'#CC0000':'transparent', textDecoration:'none', display:'block', fontFamily:"'Inter', sans-serif", fontSize:15, fontWeight: isActive('/about')?700:500, borderBottom:'1px solid #f0f0f0' }} onClick={() => setMenuOpen(false)}>ℹ️ About Us</Link>
          <Link to="/contact" style={{ padding:'12px 20px', color: isActive('/contact')?'#fff':'#222', backgroundColor: isActive('/contact')?'#CC0000':'transparent', textDecoration:'none', display:'block', fontFamily:"'Inter', sans-serif", fontSize:15, fontWeight: isActive('/contact')?700:500, borderBottom:'1px solid #f0f0f0' }} onClick={() => setMenuOpen(false)}>📞 Contact</Link>
          <Link to="/faqs"    style={{ padding:'12px 20px', color: isActive('/faqs')?'#fff':'#222', backgroundColor: isActive('/faqs')?'#CC0000':'transparent', textDecoration:'none', display:'block', fontFamily:"'Inter', sans-serif", fontSize:15, fontWeight: isActive('/faqs')?700:500, borderBottom:'1px solid #f0f0f0' }} onClick={() => setMenuOpen(false)}>❓ FAQs</Link>
          <div style={{ height:1, background:'#e5e7eb', margin:'4px 0' }} />
          {customer ? (
            <div style={{ padding:'10px 20px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:13, color:'#CC0000', fontWeight:700 }}>👤 {customer.name.split(' ')[0]}</span>
              <button onClick={handleLogout} style={{ background:'none', border:'1px solid #CC0000', color:'#CC0000', borderRadius:6, padding:'4px 10px', fontSize:12, cursor:'pointer' }}>Sign Out</button>
            </div>
          ) : (
            <div style={{ padding:'10px 20px', display:'flex', gap:8 }}>
              <button onClick={() => openAuth('login')}  style={{ flex:1, padding:'9px', borderRadius:6, border:'1px solid #CC0000', background:'transparent', color:'#CC0000', fontWeight:700, fontSize:13, cursor:'pointer' }}>Sign In</button>
              <button onClick={() => openAuth('signup')} style={{ flex:1, padding:'9px', borderRadius:6, border:'none', background:'#CC0000', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>Join Free</button>
            </div>
          )}
          {/* Contact info */}
          <div style={{ padding:'8px 20px 14px', background:'#f9f9f9', display:'flex', flexDirection:'column', gap:4 }}>
            <a href="https://wa.me/263772758875" style={{ fontSize:12, color:'#CC0000', textDecoration:'none', fontWeight:600 }}>📞 +263 772 758 875</a>
            <a href="https://wa.me/263785661758" style={{ fontSize:12, color:'#CC0000', textDecoration:'none', fontWeight:600 }}>📞 +263 785 661 758</a>
            <a href="mailto:sales@techdavesolutions.co.zw" style={{ fontSize:12, color:'#555', textDecoration:'none' }}>✉️ sales@techdavesolutions.co.zw</a>
          </div>
        </div>

      </header>

      {/* ── Auth Modal ── */}
      {showAuth && (
        <div style={styles.overlay} onClick={() => setShowAuth(false)}>
          <div style={styles.authModal} onClick={e => e.stopPropagation()}>
            <div style={styles.authTabs}>
              <button onClick={() => { setAuthMode('login'); setAuthError(''); setAuthSuccess(''); }} style={styles.authTab(authMode==='login')}>Sign In</button>
              <button onClick={() => { setAuthMode('signup'); setAuthError(''); setAuthSuccess(''); }} style={styles.authTab(authMode==='signup')}>Create Account</button>
            </div>
            {authMode === 'signup' ? (
              <>
                <div style={styles.authHeader}><div style={{ fontSize:30 }}>🎉</div><h3 style={styles.authTitle}>Join TechDave</h3><p style={styles.authSub}>Get early access to deals & exclusive discounts</p></div>
                <div style={styles.authPerks}>{['🔥 Early access to promotions','⭐ Regular customer discounts','📦 Order history & tracking','🎁 Birthday deals'].map(p => <div key={p} style={styles.perkItem}>{p}</div>)}</div>
                <input placeholder="Full Name *"       value={authName}     onChange={e => setAuthName(e.target.value)}     style={styles.authInput} />
                <input placeholder="Email *" type="email" value={authEmail} onChange={e => setAuthEmail(e.target.value)}    style={styles.authInput} />
                <input placeholder="Phone (optional)"  value={authPhone}    onChange={e => setAuthPhone(e.target.value)}    style={styles.authInput} />
                <input placeholder="Password *" type="password" value={authPassword} onChange={e => setAuthPassword(e.target.value)} onKeyDown={e => e.key==='Enter' && handleSignup()} style={styles.authInput} />
                {authError   && <p style={styles.authError}>{authError}</p>}
                {authSuccess && <p style={styles.authSuccessMsg}>{authSuccess}</p>}
                <div style={styles.authBtns}>
                  <button onClick={() => { setShowAuth(false); resetForm(); }} style={styles.authCancel}>Cancel</button>
                  <button onClick={handleSignup} style={styles.authSubmit}>Create Account →</button>
                </div>
              </>
            ) : (
              <>
                <div style={styles.authHeader}><div style={{ fontSize:30 }}>👋</div><h3 style={styles.authTitle}>Welcome Back</h3><p style={styles.authSub}>Sign in to access your deals and account</p></div>
                <input placeholder="Email" type="email"    value={authEmail}    onChange={e => setAuthEmail(e.target.value)}    style={styles.authInput} />
                <input placeholder="Password" type="password" value={authPassword} onChange={e => setAuthPassword(e.target.value)} onKeyDown={e => e.key==='Enter' && handleLogin()} style={styles.authInput} />
                {authError   && <p style={styles.authError}>{authError}</p>}
                {authSuccess && <p style={styles.authSuccessMsg}>{authSuccess}</p>}
                <div style={styles.authBtns}>
                  <button onClick={() => { setShowAuth(false); resetForm(); }} style={styles.authCancel}>Cancel</button>
                  <button onClick={handleLogin} style={styles.authSubmit}>Sign In →</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

/* ─── Styles ─────────────────────────────────────────────── */

const styles = {
  /* Outer fixed wrapper — stacks all 3 rows */
  outerWrap: { position:'fixed', top:0, left:0, width:'100%', zIndex:1000, fontFamily:"'Inter', sans-serif" },

  /* ROW 1 — White bar */
  whiteBar: (dark) => ({ background: dark?'#111':'#fff', boxShadow: dark?'none':'0 1px 0 #e5e7eb' }),
  whiteBarInner: { maxWidth:1400, margin:'0 auto', padding:'0 24px', height:76, display:'flex', alignItems:'center', justifyContent:'space-between', gap:20 },

  logoWrap: { display:'flex', alignItems:'center', gap:10, textDecoration:'none', flexShrink:0 },
  logoImg:  { height:52, width:'auto', objectFit:'contain', display:'block' },
  logoText: { display:'flex', flexDirection:'column', lineHeight:1.15 },
  logoMain: { fontWeight:800, fontSize:20, color:'#CC0000', letterSpacing:1.5, fontFamily:"'League Spartan', sans-serif" },
  logoSub:  (dark) => ({ fontWeight:600, fontSize:10, color: dark?'#aaa':'#555', letterSpacing:3, textTransform:'uppercase', fontFamily:"'League Spartan', sans-serif" }),

  searchBarWrap: (dark) => ({ flex:'1 1 auto', minWidth:0, maxWidth:600, display:'flex', alignItems:'center', border: dark?'2px solid #333':'2px solid #e0e0e0', borderRadius:28, overflow:'hidden', background: dark?'#1e1e1e':'#fff', margin:'0 16px' }),
  searchBarInput: (dark) => ({ flex:1, minWidth:0, border:'none', outline:'none', background:'transparent', fontSize:14, padding:'11px 18px', color: dark?'#e5e5e5':'#333', fontFamily:"'Inter', sans-serif" }),
  searchBarDivider: (dark) => ({ borderLeft:`1px solid ${dark?'#333':'#e0e0e0'}`, flexShrink:0 }),
  searchBarBtn: { flexShrink:0, border:'none', background:'#CC0000', color:'#fff', width:48, height:48, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' },

  categoryBtn: (dark) => ({ display:'flex', alignItems:'center', height:48, padding:'0 14px', border:'none', background:'transparent', color: dark?'#ccc':'#555', fontSize:12, fontWeight:700, letterSpacing:'0.04em', cursor:'pointer', whiteSpace:'nowrap', fontFamily:"'Inter', sans-serif" }),
  categoryDropdown: (dark) => ({ position:'absolute', top:'calc(100% + 4px)', right:0, minWidth:220, maxHeight:360, overflowY:'auto', backgroundColor: dark?'#1a1a1a':'#fff', border: dark?'1px solid #333':'1px solid #e5e7eb', borderRadius:10, boxShadow:'0 8px 30px rgba(0,0,0,0.15)', padding:'6px 0', zIndex:2000, display:'flex', flexDirection:'column' }),
  categoryOption: (dark) => ({ textAlign:'left', padding:'7px 14px', border:'none', background:'transparent', color: dark?'rgba(255,255,255,0.8)':'#444', fontSize:13, cursor:'pointer', fontFamily:"'Inter', sans-serif", width:'100%', transition:'background 0.1s' }),

  loginLink: (dark) => ({ fontSize:13, fontWeight:800, letterSpacing:'0.04em', color: dark?'#e5e5e5':'#111', whiteSpace:'nowrap', cursor:'pointer', fontFamily:"'Inter', sans-serif" }),
  navIconBtn: (dark) => ({ color: dark?'#e5e5e5':'#333', textDecoration:'none', fontSize:20, padding:'4px 6px', display:'flex', alignItems:'center', cursor:'pointer', background:'none', border:'none' }),
  navBadge: { position:'absolute', top:-6, right:-8, background:'#CC0000', color:'#fff', borderRadius:'50%', fontSize:10, fontWeight:700, minWidth:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 },

  /* ROW 2 — Single red bar with contact + nav + social */
  redNavBar: { background:'#CC0000' },
  redNavBarInner: { maxWidth:1400, margin:'0 auto', padding:'0 24px', height:44, display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 },
  redBarLink: { color:'#fff', fontSize:12, fontWeight:600, textDecoration:'none', whiteSpace:'nowrap', fontFamily:"'Inter', sans-serif" },
  redBarSocialIcon: { color:'#fff', textDecoration:'none', width:26, height:26, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'50%', border:'1px solid rgba(255,255,255,0.5)', transition:'background 0.15s', flexShrink:0 },

  navLink: (active) => ({
    display:'inline-flex', alignItems:'center', padding:'0 14px', height:44,
    color: '#fff',
    fontWeight: active ? 700 : 500, fontSize:14,
    textDecoration:'none', fontFamily:"'Inter', sans-serif",
    letterSpacing:'0.02em',
    borderBottom: active ? '3px solid #fff' : '3px solid transparent',
    transition:'all 0.15s',
  }),

  shopNavBtn: (active) => ({
    display:'inline-flex', alignItems:'center', padding:'0 14px', height:44,
    color: '#fff',
    fontWeight: active ? 700 : 500, fontSize:14,
    background:'none', border:'none',
    borderBottom: active ? '3px solid #fff' : '3px solid transparent',
    cursor:'pointer', fontFamily:"'Inter', sans-serif",
    letterSpacing:'0.02em', transition:'all 0.15s',
  }),

  shopMegaMenu: { position:'absolute', top:'100%', left:0, display:'flex', backgroundColor:'#fff', border:'1px solid #e5e7eb', borderRadius:'0 0 12px 12px', boxShadow:'0 8px 30px rgba(0,0,0,0.15)', zIndex:2000, minWidth:480, overflow:'hidden' },
  shopMegaLeft: { width:220, flexShrink:0, borderRight:'1px solid #f0f0f0', paddingTop:4, paddingBottom:8 },
  shopMegaRight: { flex:1, padding:'4px 8px 8px', backgroundColor:'#fafafa' },
  shopDropdown: { position:'absolute', top:'100%', left:0, minWidth:220, backgroundColor:'#fff', border:'1px solid #e5e7eb', borderRadius:'0 0 12px 12px', boxShadow:'0 8px 30px rgba(0,0,0,0.15)', padding:'8px', zIndex:2000, display:'flex', flexDirection:'column', gap:2 },
  shopDropdownHeader: { fontSize:11, fontWeight:800, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase', padding:'6px 12px 4px', fontFamily:"'Inter', sans-serif" },
  shopDropdownItem: { display:'flex', alignItems:'center', gap:10, padding:'9px 12px', borderRadius:8, textDecoration:'none', color:'#1a1a2e', fontSize:14, fontWeight:500, fontFamily:"'Inter', sans-serif", transition:'all 0.12s', background:'transparent' },
  shopDropdownDivider: { height:1, background:'#f0f0f0', margin:'4px 0' },

  /* Account dropdown */
  accountDropdown: (dark) => ({ position:'absolute', top:'100%', right:0, width:270, backgroundColor: dark?'#1a1a1a':'#fff', border: dark?'1px solid #333':'1px solid #e5e7eb', borderRadius:12, boxShadow:'0 8px 30px rgba(0,0,0,0.15)', padding:'18px 14px 12px', zIndex:2000, display:'flex', flexDirection:'column', gap:10, marginTop:6 }),
  dropdownTitle: (dark) => ({ fontSize:18, fontWeight:800, color: dark?'#fff':'#111', textAlign:'center', fontFamily:"'Inter', sans-serif" }),
  dropdownSignIn: { display:'block', padding:'10px', borderRadius:8, background:'#111', color:'#fff', fontWeight:700, fontSize:14, textAlign:'center', border:'none', cursor:'pointer', fontFamily:"'Inter', sans-serif", width:'100%' },
  dropdownCreateBtn: { display:'block', padding:'9px', borderRadius:8, border:'1.5px solid #d1d5db', background:'transparent', color:'#111', fontWeight:600, fontSize:14, textAlign:'center', cursor:'pointer', fontFamily:"'Inter', sans-serif", width:'100%' },
  dropdownSubtext: { fontSize:12, color:'#6b7280', textAlign:'center', lineHeight:1.5, margin:0, fontFamily:"'Inter', sans-serif" },
  dropdownDivider: { height:1, background:'#e5e7eb', margin:'2px 0' },
  dropdownItem: (dark) => ({ display:'flex', alignItems:'center', gap:10, padding:'8px', borderRadius:8, textDecoration:'none', color: dark?'#e5e5e5':'#111', fontSize:13, fontWeight:500, fontFamily:"'Inter', sans-serif" }),
  dropdownLogoutBtn: { padding:'8px', borderRadius:8, border:'1px solid #e5e7eb', background:'transparent', color:'#CC0000', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:"'Inter', sans-serif", width:'100%' },

  /* Cart dropdown */
  cartDropdown: (dark) => ({ position:'absolute', top:'100%', right:0, width:300, backgroundColor: dark?'#1a1a1a':'#fff', border: dark?'1px solid #333':'1px solid #e5e7eb', borderRadius:12, boxShadow:'0 8px 30px rgba(0,0,0,0.15)', padding:'14px', zIndex:2000, display:'flex', flexDirection:'column', gap:10, marginTop:6 }),
  cartDropdownTitle: (dark) => ({ fontSize:15, fontWeight:800, color: dark?'#fff':'#111', fontFamily:"'Inter', sans-serif", display:'flex', alignItems:'center', gap:6 }),
  cartCountBadge: { background:'#CC0000', color:'#fff', borderRadius:'50%', width:18, height:18, fontSize:10, fontWeight:900, display:'inline-flex', alignItems:'center', justifyContent:'center' },
  cartEmpty: { display:'flex', flexDirection:'column', alignItems:'center', gap:8, padding:'16px 0' },
  cartShopBtn: { padding:'8px 20px', borderRadius:8, border:'none', background:'#CC0000', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:"'Inter', sans-serif" },
  cartItemsList: { display:'flex', flexDirection:'column', gap:8, maxHeight:200, overflowY:'auto' },
  cartItemRow: (dark) => ({ display:'flex', alignItems:'center', gap:10, padding:'6px 0', borderBottom: dark?'1px solid #2a2a2a':'1px solid #f3f4f6' }),
  cartItemImg: { width:44, height:44, borderRadius:6, border:'1px solid #e5e7eb', backgroundColor:'#f9fafb', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' },
  cartItemName: (dark) => ({ fontSize:12, fontWeight:600, color: dark?'#e5e5e5':'#111', fontFamily:"'Inter', sans-serif", overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }),
  cartItemPrice: { fontSize:12, color:'#CC0000', fontWeight:700, fontFamily:"'Inter', sans-serif", marginTop:2 },
  cartRemoveBtn: { background:'none', border:'none', color:'#9ca3af', fontSize:12, cursor:'pointer', padding:'2px 4px', flexShrink:0 },
  cartDropdownDivider: { height:1, background:'#e5e7eb' },
  cartTotalRow: (dark) => ({ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'2px 0' }),
  cartViewBtn: { padding:'10px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#CC0000,#990000)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:"'Inter', sans-serif", width:'100%' },

  /* Hamburger + mobile menu */
  hamburger: (dark) => ({ flexDirection:'column', gap:5, background:'none', border:'none', cursor:'pointer', padding:6 }),
  bar: (dark) => ({ width:22, height:2, background: dark?'#e5e5e5':'#333', borderRadius:2, display:'block' }),
  mobileMenu: (dark, open) => ({ display:'flex', flexDirection:'column', overflow:'hidden', maxHeight: open?640:0, transition:'max-height 0.3s ease', background: dark?'#111':'#fff', borderTop: open?(dark?'1px solid #222':'1px solid #eee'):'none' }),
  mobileLink: (dark, active) => ({ padding:'10px 14px', color: active?'#fff':(dark?'rgba(255,255,255,0.85)':'#222'), backgroundColor: active?'#CC0000':'transparent', textDecoration:'none', display:'block', fontFamily:"'Inter', sans-serif", fontSize:14, fontWeight: active?700:500 }),

  /* Auth modal */
  overlay:   { position:'fixed', inset:0, zIndex:9999, backgroundColor:'rgba(0,0,0,0.85)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 },
  authModal: { backgroundColor:'#1a1a1a', borderRadius:20, padding:20, width:'100%', maxWidth:400, border:'1px solid rgba(204,0,0,0.3)', display:'flex', flexDirection:'column', gap:10, maxHeight:'92vh', overflowY:'auto' },
  authTabs:  { display:'flex', borderRadius:10, overflow:'hidden', border:'1px solid rgba(255,255,255,0.1)' },
  authTab:   (active) => ({ flex:1, padding:'10px', border:'none', cursor:'pointer', background: active?'linear-gradient(135deg,#CC0000,#990000)':'transparent', color: active?'#fff':'rgba(255,255,255,0.5)', fontWeight:700, fontSize:13, fontFamily:"'Inter', sans-serif" }),
  authHeader:{ textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:4 },
  authTitle: { fontSize:20, fontWeight:800, color:'#fff', margin:0, fontFamily:"'Inter', sans-serif" },
  authSub:   { fontSize:13, color:'rgba(255,255,255,0.5)', margin:0, fontFamily:"'Inter', sans-serif" },
  authPerks: { background:'rgba(204,0,0,0.08)', border:'1px solid rgba(204,0,0,0.15)', borderRadius:10, padding:'10px 12px', display:'flex', flexDirection:'column', gap:4 },
  perkItem:  { fontSize:12, color:'rgba(255,255,255,0.8)', fontWeight:500, fontFamily:"'Inter', sans-serif" },
  authInput: { backgroundColor:'#242424', border:'1.5px solid rgba(255,255,255,0.1)', borderRadius:10, padding:'11px 12px', color:'#fff', fontSize:14, outline:'none', width:'100%', fontFamily:"'Inter', sans-serif" },
  authError: { color:'#f87171', fontSize:13, fontWeight:600, margin:0, fontFamily:"'Inter', sans-serif" },
  authSuccessMsg: { color:'#4ade80', fontSize:13, fontWeight:600, margin:0, textAlign:'center', fontFamily:"'Inter', sans-serif" },
  authBtns:  { display:'flex', gap:10 },
  authCancel:{ flex:1, padding:'11px', borderRadius:10, border:'1.5px solid rgba(255,255,255,0.15)', background:'transparent', color:'#fff', fontWeight:600, cursor:'pointer', fontFamily:"'Inter', sans-serif" },
  authSubmit:{ flex:1, padding:'11px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#CC0000,#990000)', color:'#fff', fontWeight:700, cursor:'pointer', fontFamily:"'Inter', sans-serif" },
};

export default Header;
